import '@mantine/dropzone/styles.css';

import {
  Button,
  Container,
  Group,
  LoadingOverlay,
  Select,
  Stack,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import {
  createBook,
  getBookById,
  updateBook,
} from '@/entities/books/api/books-api';
import { Book, SaveBook } from '@/entities/books/model/types';
import { getPublishers } from '@/entities/publisher/api/publisher-api';
import { IPublisher } from '@/entities/publisher/model/types';
import { useDirtyForm } from '@/shared/hooks/use-dirty-form';
import {
  useCurrentProject,
  useProjectPath,
} from '@/shared/projects/project-path';

import { BookCategoriesSelect } from './book-categories-select';
import { BookImage } from './book-image';
import { BookLessonsList } from './book-lessons-list';
import { BookPublishersSelect } from './book-publishers-select';
import { BooksAuthors } from './books-authors';
import { KitabaBookDetailPage } from './kitaba-book-detail-page';

const bookFormSchema = z.object({
  title: z.string().min(1, 'Название не может быть пустым'),
  categoryIds: z.array(z.coerce.number()),
  authorId: z.number().min(1, 'Автор обязателен для выбора'),
  publisherId: z.number().min(1, 'Издательство обязательно для выбора'),
  translatorName: z.string().optional(),
  readerName: z.string().optional(),
  description: z.string().optional(),
  isPublished: z.boolean(),
});

const initialBookInfo: Book = {
  id: -1,
  title: '',
  categoryIds: [],
  authorId: 0,
  translatorName: '',
  readerName: '',
  description: '',
  isPublished: false,
  imgUrl: '',
  circleImgUrl: '',
  imagePath: '',
  circleImagePath: '',
  publisherId: 0,
};

export const BookDetailPage = () => {
  const project = useCurrentProject();
  return project === 'kitaba' ? (
    <KitabaBookDetailPage />
  ) : (
    <HatfaBookDetailPage />
  );
};

const HatfaBookDetailPage = () => {
  const [bookInfo, setBookInfo] = useState<Book>(initialBookInfo);
  const navigate = useNavigate();
  const withProject = useProjectPath();
  const { bookId } = useParams();
  const [, setPublishersData] = useState<IPublisher>();

  const isNewBook = !bookId || bookId === 'new';
  const headerTitle = isNewBook ? 'Добавление книги' : 'Редактирование книги';

  const fetchBookPublishersData = async () => {
    try {
      const publishersResponse = await getPublishers();

      setPublishersData(publishersResponse);
    } catch (error) {
      console.error('Ошибка при загрузке списка издательств', error);
    }
  };

  const fetchBookData = async () => {
    if (!isNewBook) {
      try {
        const bookResponse = await getBookById(bookId);
        setBookInfo(bookResponse);
        form.setValues(bookResponse);
      } catch (error) {
        console.error('Ошибка при загрузке книги', error);
      }
    }
  };

  useEffect(() => {
    fetchBookPublishersData();
    fetchBookData();
  }, [bookId]);

  const form = useForm<Book>({
    validateInputOnBlur: true,
    initialValues: initialBookInfo,
    validate: zodResolver(bookFormSchema),
  });
  useDirtyForm(form.isDirty());

  const handleSubmit = async (values: typeof form.values) => {
    const formattedData: SaveBook = {
      id: isNewBook ? -1 : bookInfo.id,
      title: values.title,
      categoryIds: values.categoryIds
        ? values.categoryIds.map((id: number) => Number(id))
        : [],
      authorId: Number(values.authorId),
      translatorName: values.translatorName || '',
      readerName: values.readerName || '',
      description: values.description || '',
      isPublished: values.isPublished || false,
      imgUrl: bookInfo.imagePath,
      circleImgUrl: bookInfo.circleImagePath,
      publisherId: Number(values.publisherId),
    };

    try {
      if (isNewBook) {
        await createBook(formattedData);
      } else {
        await updateBook(bookId, formattedData);
      }
      await navigate(withProject('/books'));
    } catch (error) {
      console.error('Ошибка при сохранении книги:', error);
    }
  };

  const handleCancel = () => {
    navigate(withProject(`/books/${bookId}/description`));
  };

  return (
    <Container size={1400} mt={50}>
      <Title order={1} mb={24}>
        {headerTitle}
      </Title>
      <LoadingOverlay
        visible={false}
        zIndex={1000}
        overlayProps={{ radius: 'sm', blur: 2 }}
      />
      <Stack>
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <TextInput
            mb={20}
            label="Название"
            key={form.key('title')}
            placeholder="Введите название книги"
            {...form.getInputProps('title')}
            required
          />
          <BookCategoriesSelect setBookInfo={setBookInfo} form={form} />
          <BooksAuthors setBookInfo={setBookInfo} form={form} />
          <BookPublishersSelect setBookInfo={setBookInfo} form={form} />
          <TextInput
            mb={20}
            label="Переводчик"
            key={form.key('translatorName')}
            placeholder="Введите имя переводчика"
            {...form.getInputProps('translatorName')}
          />
          <TextInput
            mb={20}
            label="Диктор"
            key={form.key('readerName')}
            placeholder="Введите имя диктора"
            {...form.getInputProps('readerName')}
          />
          <Textarea
            mb={20}
            label="Описание"
            key={form.key('description')}
            name="description"
            placeholder="Введите текст"
            {...form.getInputProps('description')}
            autosize
            minRows={3}
            resize="none"
          />
          <Select
            mb={20}
            label="Статус"
            key={form.key('isPublished')}
            placeholder="Выберите статус"
            {...form.getInputProps('isPublished')}
            value={form.values.isPublished ? '1' : '0'}
            onChange={(value) =>
              form.setFieldValue('isPublished', value === '1')
            }
            data={[
              {
                value: '0',
                label: 'Черновик',
              },
              {
                value: '1',
                label: 'Опубликовано',
              },
            ]}
          />

          <BookImage bookInfo={bookInfo} setBookInfo={setBookInfo} />

          <Group my={32}>
            <Button
              type="submit"
              disabled={!bookInfo.imgUrl || !bookInfo.circleImgUrl}
            >
              Сохранить
            </Button>
            <Button onClick={handleCancel} variant="outline" color="grey">
              Отменить
            </Button>
          </Group>
        </form>
      </Stack>

      <BookLessonsList />
    </Container>
  );
};
